// src/utils/logPageView.js
//
// Writes ONE record per page view to the global `pageVisits` collection.
//
// It used to also write to `users/{uid}/pageLogs`. So did usePageTracking,
// which runs globally for every route — so any page calling logPageView()
// logged itself to pageLogs TWICE, and React 18 StrictMode double-invokes
// effects in development, making it four. Page-view counts were inflated and
// the console filled with "Document already exists" as the duplicate writes
// raced each other.
//
// Split cleanly instead:
//   logPageView()     -> pageVisits           (global admin feed)
//   usePageTracking() -> users/{uid}/pageLogs (per-user history)
//
// Neither writes the other's collection.

import {
  getFirestore,
  doc,
  collection,
  addDoc,
  getDoc,
  serverTimestamp,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { shouldLog } from "./pageLogDedupe";

export async function logPageView(path, meta = {}) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;

    // Module-level guard, so it survives the unmount/remount that StrictMode
    // performs. A ref inside the component would be recreated and wouldn't
    // catch the second pass.
    if (!shouldLog(`visit:${user.uid}:${path}`)) return;

    const db = getFirestore();

    const userRef = doc(db, "users", user.uid);
    const userSnap = await getDoc(userRef);
    const profile = userSnap.exists() ? userSnap.data() : {};

    await addDoc(collection(db, "pageVisits"), {
      userId: user.uid,
      firstName: profile.firstName || "",
      lastName: profile.lastName || "",
      schoolName: profile.schoolName || "",
      role: profile.role || "",
      path,
      title: meta.title || document.title || "",
      referrer: meta.referrer || document.referrer || "",
      device: meta.device || navigator.userAgent || "",
      ts: serverTimestamp(),
    });
  } catch (err) {
    // "already-exists" means the write reached Firestore and the document is
    // there — the SDK retried a mutation whose acknowledgement was lost, and
    // the retry was refused because your rules are create-only. The record is
    // correct; only the acknowledgement went missing. Not worth a console
    // entry, and definitely not worth alarming anyone.
    if (err?.code === "already-exists") return;

    // Analytics must never be load-bearing. A failed log should not surface
    // to the person or interrupt the page.
    console.warn("Page view not logged:", err?.message || err);
  }
}
